import { Plus, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "../../primitives/Button";
import { IconButton } from "../../primitives/IconButton";
import { ProcessStepCard } from "./ProcessStepCard";
import type { ProcessStepTemplate, ProcessActivityTemplate, ProcessTriggerTemplate } from "../../../api/processes";

type ProcessStepsListProps = {
  steps: (ProcessStepTemplate & {
    activities?: ProcessActivityTemplate[];
    triggers?: ProcessTriggerTemplate[];
  })[];
  canEdit: boolean;
  onAddStep: () => void;
  onEditStep: (step: ProcessStepTemplate) => void;
  onDeleteStep: (stepId: string) => void;
  onReorderStep: (stepId: string, direction: "up" | "down") => void;
  expandedSteps: Set<string>;
  onToggleStep: (stepId: string) => void;
};

export function ProcessStepsList({
  steps,
  canEdit,
  onAddStep,
  onEditStep,
  onDeleteStep,
  onReorderStep,
  expandedSteps,
  onToggleStep,
}: ProcessStepsListProps) {
  const sortedSteps = [...steps].sort((a, b) => a.order_index - b.order_index);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-md font-medium text-text">Étapes du processus</h3>
        {canEdit && (
          <Button
            label="Ajouter une étape"
            intent="default"
            size="sm"
            onClick={onAddStep}
            icon={<Plus size={16} />}
          />
        )}
      </div>

      {sortedSteps.length === 0 ? (
        <div className="p-4 border border-border rounded-lg text-center text-muted-foreground">
          Aucune étape définie. Cliquez sur "Ajouter une étape" pour commencer.
        </div>
      ) : (
        <div className="space-y-2">
          {sortedSteps.map((step, index) => (
            <div key={step.id} className="relative">
              {canEdit && index > 0 && (
                <div className="absolute -top-2 right-2 z-10">
                  <IconButton
                    icon={<ArrowUp size={14} />}
                    ariaLabel="Déplacer vers le haut"
                    intent="ghost"
                    size="sm"
                    onClick={() => onReorderStep(step.id, "up")}
                  />
                </div>
              )}
              {canEdit && index < sortedSteps.length - 1 && (
                <div className="absolute -top-2 right-12 z-10">
                  <IconButton
                    icon={<ArrowDown size={14} />}
                    ariaLabel="Déplacer vers le bas"
                    intent="ghost"
                    size="sm"
                    onClick={() => onReorderStep(step.id, "down")}
                  />
                </div>
              )}
              <ProcessStepCard
                step={{
                  id: step.id,
                  title: step.title,
                  description: step.description,
                  order_index: step.order_index,
                  sla_days: step.sla_days,
                  completion_policy: step.completion_policy,
                }}
                onEdit={canEdit ? () => onEditStep(step) : undefined}
                onDelete={canEdit ? () => onDeleteStep(step.id) : undefined}
                expanded={expandedSteps.has(step.id)}
                onToggle={onToggleStep}
                activities={step.activities || []}
                triggers={step.triggers || []}
                entryConditions={step.entry_conditions}
                exitConditions={step.exit_conditions}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
